import { Button, Modal, message } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { CustomerTypeEnum, useSetUserCloak } from '../../../api'
import { CustomersSelector } from '../../components/selectors/CustomersSelector'

type Props = {
  open: boolean
  onClose(): void
}

type Selected = { id: string; name: string }

const SELECT_FIELD_ID = 'supplier-cloak-select'

const SUBMIT_FAILURE_COPY = 'Failed to cloak into the selected supplier. Please try again.'

/**
 * #7806 — the supplier-customer cloak dialog: select, then confirm.
 *
 * Two steps because Appian's cloaking page has exactly two (R7, Appian parity). Sends the EXISTING
 * `updateCloak` document through the EXISTING `useSetUserCloak` hook, with new variables
 * (`{ cloakCustomerId }` — no `cloakUserId` key). No new GraphQL document exists anywhere in this
 * story, which is why codegen's document set is unchanged.
 *
 * The picker reads the `customers` query via `CustomersSelector` — NEVER `customersWithStats` (R5).
 *
 * ⚠️ The confirm step is carried by `confirming: Selected | undefined`, not by a `'select' | 'confirm'`
 * string. That makes "on the confirm step with nothing selected" unrepresentable, so the submit path
 * needs no unreachable `if (!selected) return` guard to satisfy the type checker.
 *
 * ⚠️ CLOSING, IN FLIGHT OR NOT — the contract, in three lines, and the reasoning that picked it.
 *
 *  1. **The dialog is ALWAYS closable.** Every close path — the X, Esc, the mask, `No`, `Cancel` —
 *     works at all times. There is no state in which the dialog cannot be dismissed, in flight or
 *     not. This is ruling R4's lockout rule applied to the dialog itself: the feature exists so an
 *     admin is never trapped, and a modal that refuses to close is that trap one dialog smaller.
 *  2. **A late SUCCESS reloads, unconditionally** — even if the admin closed the dialog while the
 *     request was out. The server row really did change, so the client must not go on rendering the
 *     admin's old identity: without the reload the admin is cloaked server-side while the chrome
 *     still shows admin tools, no `Cloaked` tag and no `Exit Cloaking`, recoverable only by a manual
 *     reload. The reload is the correction, not a courtesy.
 *  3. **A late FAILURE is dropped**, silently: no toast landing on a page the admin has moved on to,
 *     and no state write behind a dialog that has already been closed (or reopened and reset).
 *     TWO conditions distinguish the two, and BOTH are needed — `liveRef` alone is not enough:
 *       - `liveRef` — the dialog's own on-screen-ness. Closed (or unmounted) ⇒ drop.
 *       - `attemptRef` — WHICH attempt this result belongs to. A reopen bumps it (§4.2), so a
 *         rejection from an abandoned attempt finds a dialog that is on screen again but is no
 *         longer waiting for it, and is dropped. Without this, closing mid-flight and reopening
 *         would toast the first attempt's failure onto a fresh dialog sitting on the select step,
 *         and its `setSubmitting(false)` would clear the `Yes` spinner of a SECOND request that is
 *         still outstanding.
 *
 * ⚠️ A SECOND `updateCloak` IS REACHABLE, AND THAT IS ACCEPTED — do not "fix" it back. Close
 * mid-flight, reopen (which resets `submitting`), confirm again, and a second request goes out behind
 * the first. Both are the same admin cloaking, whichever settles last wins on the server, and
 * whichever succeeds reloads the page under the context that write produced — so the UI cannot end up
 * disagreeing with the server. An earlier round defended against this by gating every close path on
 * `submitting` and then bounding that gate with a timer; the gate produced exactly the stale-identity
 * failure requirement 2 names, and the bound produced its own unclosable and late-toast states. The
 * cheap correct outcome beat the expensive one.
 */
export const SupplierCloakDialog: React.FC<Props> = ({ open, onClose }) => {
  const [selected, setSelected] = useState<Selected | undefined>(undefined)
  const [confirming, setConfirming] = useState<Selected | undefined>(undefined)
  const [search, setSearch] = useState<string | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  // "Is this dialog still on screen?" — read only by the failure branch, to decide whether a result
  // still has a dialog to report to. The cleanup also covers UNMOUNT (a route change mid-flight), so
  // no branch can write state into a component that is gone.
  const liveRef = useRef(open)
  // "Which attempt is this dialog waiting for?" — a monotonic counter, bumped on every submit AND on
  // every reopen. The failure branch compares the value it captured at submit against the current
  // one, so only the CURRENT attempt's rejection is surfaced. A bare `liveRef` check cannot do this:
  // after close → reopen it reads `true` again and would surface an abandoned attempt's failure.
  const attemptRef = useRef(0)
  const [updateCloak] = useSetUserCloak()

  useEffect(() => {
    liveRef.current = open
    return () => {
      liveRef.current = false
    }
  }, [open])

  // Reset DURING the render in which `open` flips to true, not in an effect after it — an effect runs
  // after the reopened dialog has already painted, so the previous attempt's step would show for one
  // frame. (React's documented "adjust state when a prop changes" pattern.)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setSelected(undefined)
      setConfirming(undefined)
      setSearch(undefined)
      setSubmitting(false)
      // Bumped with the rest of the reset: the reopened dialog is waiting for nothing, so any
      // attempt still outstanding from the previous session is now stale and its failure is dropped.
      //
      // ⚠️ YES, THIS IS A SIDE EFFECT IN THE RENDER BODY, AND IT IS DELIBERATE — do not "fix" it into
      // an effect. Under `StrictMode` React double-invokes the render, so a reopen bumps this by TWO
      // instead of one. That is harmless here because the token is only ever compared for EQUALITY
      // (`attemptRef.current !== attempt` below), never used as a count or an index, so any strictly
      // monotonic bump separates the attempts identically. Moving it into an effect would defeat the
      // point of the whole block: the reset has to land in the render that reopens the dialog, or the
      // previous attempt's step paints for one frame (see the comment above).
      attemptRef.current += 1
    }
  }

  const handleConfirm = async (target: Selected) => {
    const attempt = attemptRef.current + 1
    attemptRef.current = attempt
    setSubmitting(true)

    try {
      const { data } = await updateCloak({ variables: { input: { cloakCustomerId: target.id } } })
      // Success is `data.updateCloak === true` (spec §4.2), not merely "the promise resolved".
      if (data?.updateCloak !== true) {
        throw new Error('updateCloak did not confirm the cloak')
      }
      // The same full reload the hospital path performs, so the next `me` is evaluated under the new
      // context rather than the stale one. UNCONDITIONAL — see requirement 2 on the component.
      window.location.href = '/'
    } catch {
      // Dropped if the dialog is gone (closed or unmounted) OR if it has moved on to another
      // attempt — see requirement 3. NOT a timeout, not a bound: no timer exists here, and the
      // success path above is unconditional by design.
      if (!liveRef.current || attemptRef.current !== attempt) {
        return
      }
      // The central Apollo error link has already toasted the mapped copy; this names the action.
      message.error(SUBMIT_FAILURE_COPY)
      setSubmitting(false)
    }
  }

  const footer = confirming
    ? [
        // The HOST PAGE's labels (Users.tsx's Popconfirm uses Yes/No), not the portal's OK/Cancel —
        // react-conventions §20.1, host-page-wins.
        <Button key="no" onClick={() => setConfirming(undefined)}>
          No
        </Button>,
        <Button key="yes" type="primary" loading={submitting} onClick={() => handleConfirm(confirming)}>
          Yes
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="continue" type="primary" disabled={!selected} onClick={() => setConfirming(selected)}>
          Continue
        </Button>,
      ]

  return (
    <Modal open={open} destroyOnClose title="Cloak into a Supplier" onCancel={onClose} footer={footer}>
      {confirming ? (
        <div>
          <h4>Cloak into {confirming.name}?</h4>
          <p>
            You will view CurvoX as {confirming.name}, with that supplier&apos;s access, until you exit cloaking. Your
            admin tools are unavailable while cloaked. You can exit at any time from Exit Cloaking in the sidebar.
          </p>
        </div>
      ) : (
        <>
          <label htmlFor={SELECT_FIELD_ID}>Supplier</label>
          <CustomersSelector
            id={SELECT_FIELD_ID}
            type={CustomerTypeEnum.Supplier}
            showSearch
            autoFocus
            labelInValue={false}
            searchValue={search}
            // ⚠️ COALESCED TO `undefined`, never stored as `''`. rc-select 14.1.18 clears the search
            // box when the dropdown closes: `BaseSelect.js:232-236`'s effect on `mergedOpen` calls
            // `onInternalSearch('')`, which reaches this handler because the controlled
            // `searchValue` still holds the typed text. MEASURED: it fires when the admin closes the
            // dropdown with text still in the box (Esc on the input, or clicking away) — not when
            // they pick an option, where rc-select clears its own search state first. A bare
            // `onSearch={setSearch}` therefore puts `searchText: ''` on the wire — harmless
            // server-side, but §1.2/§4.2 say the key is ABSENT when the box is empty, and
            // `undefined` is what drops it from the serialised variables. Locked by C5-control.
            onSearch={value => setSearch(value || undefined)}
            value={selected?.id}
            placeholder="Search suppliers by name"
            notFoundContent="No suppliers match"
            style={{ width: '100%' }}
            onChange={(value, option) => {
              const id = value as string
              // The selector already sets `title` on every Option, so the label comes from the
              // loaded rows rather than a second lookup.
              const name = (option as { title?: string } | undefined)?.title ?? id
              setSelected(id ? { id, name } : undefined)
            }}
          />
        </>
      )}
    </Modal>
  )
}
